import React, { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

const fetchThemeConfig = () => {
  const stored = localStorage.getItem("vibeTheme");
  const fallback = {
    primaryColor: "#fa8128",
    secondaryColor: "#000000",
    font: "Nunito, sans-serif",
    logoUrl: "/logo.png",
    bannerUrl: "/dashboardbanner.png",
  };
  if (!stored) return fallback;
  try {
    const parsed = JSON.parse(stored);
    return {
      ...fallback,
      ...parsed,
      logoUrl: parsed?.logoUrl && parsed.logoUrl.trim() ? parsed.logoUrl : fallback.logoUrl,
      bannerUrl: parsed?.bannerUrl && parsed.bannerUrl.trim() ? parsed.bannerUrl : fallback.bannerUrl,
    };
  } catch {
    return fallback;
  }
};

export default function LearnerLayout() {
  const [theme, setTheme] = useState(fetchThemeConfig());
  const [role, setRole] = useState("learner");

  useEffect(() => { setTheme(fetchThemeConfig()); }, []);

  useEffect(() => {
    let cancelled = false;
    const loadRole = async () => {
      const { data: userRes } = await supabase.auth.getUser();
      const user = userRes?.user;
      if (!user?.id) return;

      const { data: rows, error } = await supabase
        .from("profiles")
        .select("role")
        .eq("user_id", user.id);

      if (cancelled) return;
      if (error) { console.error("Role fetch error:", error); return; }

      const r = String(rows?.[0]?.role || "learner").toLowerCase();
      setRole(r);
    };
    loadRole();
    return () => { cancelled = true; };
  }, []);

  const navItems = [
    { name: "Dashboard", path: "/learner/dashboard" },
    { name: "Courses", path: "/learner/courses" },
    ...(role === "manager" || role === "superadmin"
      ? [{ name: "My Team", path: "/learner/team" }]
      : []),
    { name: "Profile", path: "/learner/profile" },
    ...(role === "superadmin"
      ? [{ name: "Admin Panel", path: "/admin/" }]
      : []),
  ];

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = "/login";
  };

  const roleLabel =
    role === "superadmin" ? "Super Admin" : role === "manager" ? "Manager" : null;

  return (
    <div className="flex min-h-screen" style={{ fontFamily: theme.font }}>
      <aside className="w-60 bg-white border-r shadow-sm flex flex-col">
        {/* Logo / Brand */}
        <NavLink to="/learner/dashboard" className="block px-6 pt-6 pb-4 hover:opacity-90">
          <img src={theme.logoUrl} alt="My Co-op Learning" className="h-12 mb-1 object-contain" />
          <div className="text-lg font-semibold text-gray-700 tracking-tight">My Co-op Learning</div>
          {roleLabel && <div className="text-xs text-gray-400 mt-1">{roleLabel}</div>}
        </NavLink>

        {/* Navigation */}
        <nav className="flex-1 flex flex-col space-y-2 px-6">
          {navItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.path}
              className={({ isActive }) =>
                `py-2 px-3 rounded-md text-sm font-medium transition ${
                  isActive ? "bg-gray-200 text-black" : "text-gray-600 hover:bg-gray-100"
                }`
              }
            >
              {item.name}
            </NavLink>
          ))}

          {/* Divider + Log out */}
          <div className="pt-2 mt-2 border-t border-gray-100">
            <button
              onClick={handleLogout}
              className="w-full text-left py-2 px-3 rounded-md text-sm font-medium text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
            >
              Log out
            </button>
          </div>
        </nav>
      </aside>

      <main className="flex-1 bg-gray-100">
        <Outlet />
      </main>
    </div>
  );
}
